<template>
  <div class="mt-10 select-none ai-chat-footer z-10">
    <TopicList
      v-if="topicList.length"
      :topicList="topicList"
      @item-click="handleTopicClick"
    />
    <div
      class="px-10 transition-height ease-in-out! duration-200! box-border pb-[calc(env(safe-area-inset-bottom)/2_+_25px)]"
    >
      <div
        class="rounded-12 border-none! min-h-44 bg-white"
        style="box-shadow: 0px 3px 13px 0px rgba(177, 177, 177, 0.5)"
      >
        <div
          v-show="imageList.length"
          class="border-b-1 border-b-solid border-b-#F3F3F3 px-12 pt-11 pb-14"
        >
          <Image
            v-model="imageList"
            @update:model-value="inputFocus"
            capture="camera"
          />
        </div>
        <div
          v-if="!disabled"
          :class="['h-44 gap-18 flex items-center', !holdActive && 'px-10']"
        >
          <Image
            v-show="!holdActive && !imageList.length && !isWriting"
            v-model="imageList"
            @update:model-value="inputFocus"
            capture="camera"
          >
            <div
              class="flex items-center h-full"
              v-if="!disabled && !holdActive"
            >
              <img :src="CameraImg" class="w-23.36 h-21.25" alt="camera" />
            </div>
          </Image>
          <van-field
            v-if="inputMode === InputMode.Text"
            :class="`bg-transparent! h-full flex-1 [&_textarea]:h-full! input-field flex items-center px-0!`"
            style="--van-field-text-area-min-height: auto"
            v-model="messageContent"
            rows="1"
            :placeholder="
              imageList.length ? describeYourNeedsPlaceholder : askMTPlaceholder
            "
            @focus="handleFocus(true)"
            @blur="handleFocus(false)"
            type="textarea"
            @confirm="handleSend"
            ref="inputRef"
            :disabled="disabled"
            maxlength="1000"
          />
          <template v-else>
            <Audio
              v-if="!imageList.length && supportAudio"
              v-model:hold-active="holdActive"
              @end="handleAudio"
            />
          </template>
          <div v-if="!disabled && !holdActive" class="flex items-center gap-18">
            <img
              v-if="inputMode === InputMode.Voice"
              :src="KeyboardImg"
              class="size-24"
              alt="keyboard"
              @click="handleInputModeChange(InputMode.Text)"
            />
            <template v-else>
              <img
                v-if="!imageList.length && supportAudio"
                v-show="!messageContent.trim()"
                :src="VoiceImg"
                class="size-24"
                alt="voice"
                @click="handleInputModeChange(InputMode.Voice)"
              />
            </template>
            <img
              v-if="!messageContent.trim()"
              :src="AddImg"
              :class="[
                'size-24 transform transition-transform duration-200',
                isShowMedia && 'rotate-45',
              ]"
              alt="add"
              @click="handleToggleMediaShow"
            />
            <img
              v-else
              :src="SendActiveImg"
              class="size-24"
              alt="send"
              @click="handleSend"
            />
          </div>
        </div>
      </div>
      <Media
        v-show="isShowMedia"
        v-model="imageList"
        @update:model-value="inputFocus"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import "fastclick";
import { ref, watch, nextTick, computed, onMounted } from "vue";
import SendActiveImg from "@/assets/images/ai-chat/send-active.png";
import VoiceImg from "@/assets/images/ai-chat/audio.png";
import CameraImg from "@/assets/images/ai-chat/camera.png";
import KeyboardImg from "@/assets/images/ai-chat/keyboard.png";
import AddImg from "@/assets/images/ai-chat/add.png";
import Audio from "./Audio.vue";
import Image from "./Image.vue";
import Media from "./Media.vue";
import TopicList from "./TopicList.vue";
import { Message } from "../type";
import { compareVersion, Env } from "@voghion/fe-util";
import { useI18n } from "vue-i18n";

defineProps<{
  disabled: boolean;
  topicList: string[];
}>();

const emits = defineEmits<{
  sendMessage: [message: Partial<Message>];
  audio: [audioBlob: Blob, imageList: string];
  focus: [isFocus: boolean];
}>();

enum InputMode {
  Text = "text",
  Voice = "voice",
}

const { t } = useI18n();
const askMTPlaceholder = ref(t("message.ai.askAnything"));
const describeYourNeedsPlaceholder = ref(t("message.ai.describeYourNeeds"));
const holdActive = ref(false);
const messageContent = defineModel<string>("message", {
  required: true,
});
const isFocus = ref(false);
const inputMode = ref<InputMode>(InputMode.Text);
const imageList = ref<{ url: string }[]>([
  // "https://g-images.voghion.com/productImages/135_1745292594658_voghion32x32.png",
]);
const isShowMedia = ref(false);
const supportAudio = ref(true);

const inputRef = ref<HTMLInputElement | null>(null);

const isWriting = computed(() => {
  return inputMode.value === InputMode.Text && messageContent.value;
});

defineExpose({
  inputRef,
});

const handleFocus = (_isFocus: boolean) => {
  isFocus.value = _isFocus;
  emits("focus", _isFocus);
};

const handleAudio = (audioBold: Blob) => {
  emits("audio", audioBold, imageList.value?.map((item) => item.url).join(","));
  imageList.value = [];
};

watch(
  () => imageList.value.length,
  (newVal, oldVal) => {
    if (newVal !== oldVal)
      inputMode.value = newVal ? InputMode.Text : InputMode.Voice;
  },
);

const handleSend = () => {
  const content = messageContent.value;
  if (!content.trim()) {
    return inputFocus();
  }
  emits("sendMessage", {
    reasonContent: content,
    imageList: imageList.value?.map((item) => item.url).join(","),
  });
  imageList.value = [];
};

const handleInputModeChange = async (mode: InputMode) => {
  inputMode.value = mode;
  inputFocus();
};

const handleToggleMediaShow = () => {
  isShowMedia.value = !isShowMedia.value;
};

const inputFocus = async () => {
  await nextTick();
  if (inputMode.value === InputMode.Text) {
    isShowMedia.value = false;
    inputRef.value?.focus();
  }
};

const handleTopicClick = (message: Partial<Message>) => {
  messageContent.value = message.reasonContent || "";
  emits("sendMessage", message);
};

onMounted(() => {
  const env = Env.getInstance();
  if (env.isIOS && compareVersion(env.iOSVersion, "15") === -1) {
    supportAudio.value = false;
    inputMode.value = InputMode.Text;
  }
});
</script>
